import { Component, OnInit, Input, Output, EventEmitter, HostListener, ElementRef } from '@angular/core';
import { FormBuilder, FormGroup, FormArray } from '@angular/forms';
import { Observable, throwError, Subscription } from 'rxjs';
import { catchError, debounceTime } from 'rxjs/operators';
import { HttpClient, } from '@angular/common/http';
import { Subject } from 'rxjs/Subject';
import Swal from 'sweetalert2';


import { getAcronym, checkMobileView, parseDate } from '@teem/helpers/functions';
import { InputTaxForm } from '@teem/model-forms/input-tax-form';
import { LoaderService } from '@teem/services/loader.service';
import { environment } from '@teem/environments/environment';
import { messages as i18n } from '@teem/constants/messages';
import { taxes as TaxesApi } from '@teem/constants/api';
import { TaxService } from '@teem/services/tax.service';
import { queryString } from '@teem/helpers/functions';
import { Taxes } from '@teem/models/taxes';



@Component({
  selector: 'all-input-table',
  templateUrl: './input-tax-table.component.html',
  styleUrls: ['./input-tax-table.component.scss']
})

export class InputTaxTableComponent implements OnInit {
  getAcronym = getAcronym;

  @Input() expensesList: Array<any>;
  @Input() userInfo;
    /**
   * Optional explicit style object passed from parent: <all-input-table [tableStyle]="{ 'max-width': '900px' }"></all-input-table>
   */
  @Input() tableStyle: { [k: string]: any } | null = null;
  /**
   * When true (default) the component will look for inline styles placed on the host element
   * (e.g. <all-input-table style="max-width:900px" ...>) and apply them to the internal wrapper.
   * Set to false to ignore host inline styles.
   */
  @Input() useHostStyle: boolean = true;
  @Output() perPageItems: EventEmitter<any> = new EventEmitter();
  /** Computed ngStyle object actually applied to the template wrapper */
  appliedStyle: { [k: string]: any } | null = null;
  @Output() onLoad: EventEmitter<any> = new EventEmitter();
  serverUrl: string = TaxesApi.input_taxes;
  data: Array<object> = [];
  timeFilter: string;
  search = new Subject<string>();
  searchText: string;
  itemsPerPage: any = 10;
  itemsPerPageOptions = [10, 20, 50, 100];
  paginator: any = {
    id: 'input-table-paginator',
    currentPage: 1,
    itemsPerPage: environment.pageSize,
    totalItems: 0
  }

  loading: boolean = false;
  searchSubscription: Subscription;
  dataSubscription: Subscription;
  editExpenseForm: FormGroup;
  inputTaxes = new FormArray([]);
  isMobileView: boolean = checkMobileView();
  fromDate: any = null;
  toDate: any = null;
  filterParams: object = {};

  constructor(
    private fb: FormBuilder,
    private httpClient: HttpClient,
    private loaderService: LoaderService,
    private taxService: TaxService,
    private el: ElementRef) { }

  ngOnInit(): void {
    this.searchSubscription = this.search.asObservable().pipe(debounceTime(300)).subscribe(
      (data) => {
        this.searchText = data;
        if (this.searchText.length == 0 || this.searchText.length >= 3) {
          this.getPage(1, false);
        }
      }
    );

    this.editExpenseForm = this.fb.group(new InputTaxForm(new Taxes()));

    // Compute applied styles: priority -> explicit [tableStyle] Input, otherwise host inline style
    if (this.tableStyle) {
      this.appliedStyle = this.tableStyle;
    } else if (this.useHostStyle) {
      try {
        const styleString = this.el?.nativeElement?.getAttribute && this.el.nativeElement.getAttribute('style');
        if (styleString) {
          const styleObj: any = {};
          styleString.split(';').forEach(part => {
            const kv = part.split(':');
            if (kv.length === 2) {
              const key = kv[0].trim();
              const val = kv[1].trim();
              if (key) {
                styleObj[key] = val;
              }
            }
          });
          this.appliedStyle = styleObj;
        }
      } catch (e) {
        // ignore parse errors and keep appliedStyle null
      }
    }

    this.getPage(1);
  }

  ngOnDestroy() {
    this.searchSubscription.unsubscribe()
  }

  @HostListener('window:resize', ['$event'])
  onResize(event) {
    this.isMobileView = checkMobileView();
  }

  isSubmitted(form: FormGroup) {
    return form.get('submitted').value;
  }

  resetDateFilters() {
    this.fromDate = null;
    this.toDate = null;
    this.filterParams = {};
  }

  public getPage(page: number, loading: boolean = true, additionalParams: any = null) {

    // Unselect selected items
    if (loading) {
      this.loaderService.startLoading();
      this.loading = true; // to show loading text in table
    }

    let params: any = {
      page: page,
      limit: this.paginator.itemsPerPage
    }

    if (this.searchText && this.searchText.length >= 3) {
      params.q = this.searchText;
    }

    if (this.timeFilter) {
      params.filter_by = this.timeFilter
    }

    if (additionalParams) {
      Object.assign(this.filterParams, additionalParams);
    }

    Object.assign(params, this.filterParams);

    if (params.from_date) {
      this.fromDate = parseDate(params.from_date);
    }

    if (params.to_date) {
      this.toDate = parseDate(params.to_date);
    }

    if (this.fromDate)
      params.from_date = this.fromDate;

    if (this.toDate)
      params.to_date = this.toDate;

    this.getData(params).subscribe(
      (r) => {
        if (r.data) {
          this.data = r.data;

          this.inputTaxes = new FormArray([]);

          r.data.forEach(expense => {
            this.addInputTax(expense);
          })
        }

        if (r.meta) {
          this.paginator.totalItems = r.meta.total;
          this.paginator.currentPage = r.meta.current_page;
          this.paginator.from = r.meta.from;
          this.paginator.to = r.meta.to;
          this.data = r.data;

          this.onLoad.emit({
            totalItems: r.meta.total
          });
        }

        this.loaderService.stopLoading();
        this.loading = false;
      }, (err) => {
        this.loading = false;
        this.loaderService.stopLoading()
      }
    )
  }

  getData(params): Observable<any> {
    let url = this.serverUrl;

    if (url.indexOf('?') == -1)
      url += ('?' + queryString(params));
    else
      url += ('&' + queryString(params));

    return this.httpClient.get(`${url}`).pipe(
      catchError((err: any) => {
        this.loaderService.stopLoading()
        return throwError(err);
      })
    )
  }


  addInputTax(form: any) {
    this.inputTaxes.push(
      this.fb.group(
        new InputTaxForm(
          new Taxes(form)
        )
      )
    )
  }

  onVatChange(form: FormGroup): void {
    form.get('net_vat_amount_percent').setValue((form.value.cost * form.value.vat_percentage) / 100);
  }

  onSubmitEditInputTax(form: FormGroup) {
    if (form.controls.taxable_amount.status == "INVALID") {
      form.get('taxable_amount').setValue(form.value.cost);
    }
    form.get('submitted').setValue(true);
    if (form.valid) {
      this.loaderService.startLoading();

      let fd = {
        'uuid': form.value.uuid,
        'receipt_number': form.value.receipt_number,
        'vendor_name': form.value.vendor_name,
        'vat_number': form.value.vat_number,
        'vat_percentage': form.value.vat_percentage,
      };

      this.taxService.update(fd).subscribe(data => {
        Swal.fire(i18n.done, i18n.taxes.input_tax_updated, 'success');
        form.get('submitted').setValue(false);
        this.getPage(1);
        this.loaderService.stopLoading();
      }, err => {
        this.loaderService.stopLoading();

        // Set server side error
        if (err.status === 422) {
          if (err.error && err.error.errors && Object.keys(err.error.errors).length) {
            let validationErrors = err.error.errors;
            Object.keys(validationErrors).forEach((prop) => {
              form.get(prop)?.setErrors({
                serverError: validationErrors[prop]
              })
            })
          }
        }

        Swal.fire(i18n.alert.error, i18n.getError(err), 'error');
      });
    }
  }



  cancelEditFrom(form: FormGroup) {
    let fd = form.value;
    let inputTx = this.data.find(
      (obj: any) => obj.uuid == fd.uuid
    )
    form.reset(inputTx);
  }

  onChangeItemsPerPage(event: Event, api: any) {
    console.log('child')
    const selectElement = event.target as HTMLSelectElement;
    this.perPageItems.emit({ limit: +selectElement.value });
    this.paginator.itemsPerPage = selectElement.value
  }
}
